import generateCanonicalLink from '~~/helpers/seo/generateCanonicalLink';
import generateHreflangLink from '~~/helpers/seo/generateHreflangLink';
import {
  generateMetaAppVersion,
  generateMetaCopyrightHolder,
  generateMetaCopyrightYear,
  generateMetaDescription,
  generateMetaExpondoImage,
  generateMetaExpondoOGImage,
  generateMetaIsFamilyFriendly,
  generateMetaOGDescription,
  generateMetaOGTitle,
  generateMetaRobots,
  generateMetaTitleName,
} from '~~/helpers/seo/generateMetaTags';
import type { LocalisedString } from '~~/helpers/seo/types/LocalisedString';
import { DEFAULT_META_TITLE } from '~~/constants/defaultMetaTitle';
import { i18nToCurrentDomain } from '~~/helpers/locales/i18nCurrentDetails';

import { getFirstChildOrSelf } from '~~/helpers/dataStructure/getFirstChildOrSelf';

export const useMetaExtended = ({
  metaTitle,
  isPageIndexed,
  metaDescription,
  slug,
  localisedSlugs,
  isPageHreflangLinkHidden,
  bodyAttrsClass = '',
  isPdp,
  isCategoryPage,
  imagePath,
}: {
  metaTitle: ComputedRef<string> | Ref<string>,
  isPageIndexed: ComputedRef<boolean> | Ref<boolean>,
  metaDescription?: ComputedRef<string> | Ref<string>,
  slug?: ComputedRef<string> | Ref<string>,
  localisedSlugs?: ComputedRef<LocalisedString[]> | Ref<LocalisedString[]>,
  isPageHreflangLinkHidden?: ComputedRef<boolean> | Ref<boolean>,
  bodyAttrsClass?: string,
  isPdp?: boolean,
  isCategoryPage?: boolean,
  imagePath?: ComputedRef<string> | Ref<string>,
}) => {

  const i18n = useI18n();
  const { countryCode } = useLocalization();
  const title = computed(() => metaTitle?.value || DEFAULT_META_TITLE);
  const route = useRoute();
  const currentPage = computed(() => {

    const pageParameter: string = getFirstChildOrSelf(route.query?.page) || '1';
    const parsedPage: number = parseInt(pageParameter);

    if (parsedPage > 1) {

      return parsedPage;

    } else {

      return undefined;

    }

  });

  const currentDomain = i18nToCurrentDomain(i18n) || '';
  const { languagePrefix } = usePrefix();
  const $config = useRuntimeConfig();

  const meta = computed(() => [
    generateMetaRobots(isPageIndexed.value),
    generateMetaDescription(metaDescription?.value || i18n.t('SEO meta description default')),
    generateMetaCopyrightYear(),
    generateMetaIsFamilyFriendly(),
    generateMetaExpondoImage(imagePath?.value),
    generateMetaTitleName(title.value),
    generateMetaCopyrightHolder(currentDomain),
    generateMetaAppVersion(String($config.TAG ?? '')),
    generateMetaOGTitle(title.value),
    generateMetaOGDescription(metaDescription?.value || i18n.t('SEO meta description default')),
    generateMetaExpondoOGImage(imagePath?.value),
  ]);

  const link = computed(() => [
    generateCanonicalLink({
      domain: currentDomain,
      slug: slug?.value || '',
      prefix: languagePrefix,
      pageNumber: currentPage.value,
      countryCode: countryCode.value,
      isPdp,
      isCategoryPage,
    }),
    ...generateHreflangLink(
      localisedSlugs?.value,
      {
        isPageHreflangLinkHidden: isPageHreflangLinkHidden?.value,
        isPdp,
        isCategoryPage,
      },
    ),
  ]);
  useHead(() => ({
    title: title.value,
    meta: meta.value.map(({ hid, ...rest }) => ({
      key: hid,
      'data-hid': hid,
      'data-n-head': 'ssr',
      ...rest,
    })),
    link: link.value.map((l) => {

      const hreflang = (l as unknown as { hreflang?: string }).hreflang;

      return {
        rel: l.rel,
        href: l.href,
        ...(hreflang ? { hreflang } : {}),
      };

    }),
    bodyAttrs: {
      class: bodyAttrsClass,
    },
  }));

};
